// src/context/OnboardingContext.tsx

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import { useAuth } from "./AuthContext";
import {
  getOnboarding,
  updateOnboarding as apiUpdateOnboarding,
  completeOnboarding as apiCompleteOnboarding,
  type OnboardingResponse,
  type OnboardingState,
  type OnboardingUpdatePayload,
} from "@/api/onboarding";

type OnboardingContextType = {
  /** Status from auth context (fast, available immediately) */
  onboardingStatus: string | null;
  /** Should the onboarding modal be shown? */
  shouldShowModal: boolean;
  /** Is the modal currently open? */
  isModalOpen: boolean;
  /** Open the modal */
  openModal: () => void;
  /** Close the modal */
  closeModal: () => void;
  /** Full onboarding state (lazy-loaded from API) */
  onboardingData: OnboardingResponse | null;
  /** Loading state for onboarding data */
  loading: boolean;
  /** Update onboarding state (PATCH wrapper) */
  updateOnboarding: (payload: OnboardingUpdatePayload) => Promise<OnboardingResponse>;
  /** Complete onboarding (POST /complete wrapper) */
  completeOnboarding: (status: "COMPLETED" | "SKIPPED") => Promise<void>;
  /** Refresh onboarding data from server */
  refreshOnboarding: () => Promise<void>;
  /** Are there pending source todos? */
  hasPendingSourceTodos: boolean;
};

const STORAGE_KEY = "verity_onboarding_enabled";

function readOnboardingFlag(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

const OnboardingContext = createContext<OnboardingContextType | null>(null);

const NOOP_CONTEXT: OnboardingContextType = {
  onboardingStatus: null,
  shouldShowModal: false,
  isModalOpen: false,
  openModal: () => {},
  closeModal: () => {},
  onboardingData: null,
  loading: false,
  updateOnboarding: () => Promise.resolve({} as OnboardingResponse),
  completeOnboarding: () => Promise.resolve(),
  refreshOnboarding: () => Promise.resolve(),
  hasPendingSourceTodos: false,
};

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [onboardingEnabled, setOnboardingEnabled] = useState(readOnboardingFlag);

  // Listen for toggle changes from other components
  useEffect(() => {
    const handler = () => setOnboardingEnabled(readOnboardingFlag());
    window.addEventListener("verity_onboarding_toggle", handler);
    return () => window.removeEventListener("verity_onboarding_toggle", handler);
  }, []);

  if (!onboardingEnabled) {
    return (
      <OnboardingContext.Provider value={NOOP_CONTEXT}>
        {children}
      </OnboardingContext.Provider>
    );
  }

  return <OnboardingProviderInner>{children}</OnboardingProviderInner>;
}

function OnboardingProviderInner({ children }: { children: ReactNode }) {
  const { activeAccount, user, refreshUser } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [onboardingData, setOnboardingData] = useState<OnboardingResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);

  const onboardingStatus = activeAccount?.onboardingStatus ?? null;
  const userRole = activeAccount?.role ?? null;
  const isAdmin = userRole === "OWNER" || userRole === "ADMIN";

  const shouldShowModal = useMemo(() => {
    if (!onboardingStatus || !isAdmin) return false;
    return onboardingStatus === "NOT_STARTED";
  }, [onboardingStatus, isAdmin]);

  // Lazy-load onboarding data when modal should show or status indicates onboarding
  const fetchOnboarding = useCallback(async () => {
    if (!activeAccount) return;
    setLoading(true);
    try {
      const data = await getOnboarding();
      setOnboardingData(data);
    } catch (err) {
      console.error("Failed to fetch onboarding state:", err);
    } finally {
      setLoading(false);
    }
  }, [activeAccount]);

  // Auto-fetch when status changes to a relevant state
  useEffect(() => {
    if (
      onboardingStatus === "NOT_STARTED" ||
      onboardingStatus === "IN_PROGRESS" ||
      onboardingStatus === "COMPLETED"
    ) {
      fetchOnboarding();
    }
  }, [onboardingStatus, fetchOnboarding]);

  // Auto-complete: if DWH is active and data sources exist outside the setup flow,
  // mark onboarding as completed so the user isn't nagged.
  useEffect(() => {
    if (
      !onboardingData ||
      (onboardingStatus !== "NOT_STARTED" && onboardingStatus !== "IN_PROGRESS")
    ) {
      return;
    }
    const hasDwh = onboardingData.dwh_provisioning_status === "ACTIVE";
    const hasSources = (onboardingData.onboarding_state?.source_todos ?? []).some(
      (t) => t.status === "connected"
    );
    // If account already has an active DWH, that's enough to consider setup done
    if (hasDwh) {
      apiCompleteOnboarding("COMPLETED")
        .then(() => refreshUser())
        .catch((err) => console.error("Failed to auto-complete onboarding:", err));
    }
  }, [onboardingData, onboardingStatus, refreshUser]);

  // Auto-open modal for new accounts
  useEffect(() => {
    if (shouldShowModal && !hasAutoOpened && !loading) {
      setIsModalOpen(true);
      setHasAutoOpened(true);
    }
  }, [shouldShowModal, hasAutoOpened, loading]);

  // Reset auto-open state when account changes
  useEffect(() => {
    setHasAutoOpened(false);
    setOnboardingData(null);
  }, [activeAccount?.id]);

  const openModal = useCallback(() => setIsModalOpen(true), []);
  const closeModal = useCallback(() => setIsModalOpen(false), []);

  const updateOnboarding = useCallback(
    async (payload: OnboardingUpdatePayload) => {
      const result = await apiUpdateOnboarding(payload);
      setOnboardingData(result);
      return result;
    },
    []
  );

  const completeOnboarding = useCallback(
    async (status: "COMPLETED" | "SKIPPED") => {
      await apiCompleteOnboarding(status);
      setIsModalOpen(false);
      // Refresh auth to get updated onboardingStatus
      await refreshUser();
      // Refresh onboarding data
      await fetchOnboarding();
    },
    [refreshUser, fetchOnboarding]
  );

  const refreshOnboarding = useCallback(async () => {
    await fetchOnboarding();
  }, [fetchOnboarding]);

  const hasPendingSourceTodos = useMemo(() => {
    const todos = onboardingData?.onboarding_state?.source_todos;
    if (!todos || todos.length === 0) return false;
    return todos.some((t) => t.status === "pending");
  }, [onboardingData]);

  const value = useMemo<OnboardingContextType>(
    () => ({
      onboardingStatus,
      shouldShowModal,
      isModalOpen,
      openModal,
      closeModal,
      onboardingData,
      loading,
      updateOnboarding,
      completeOnboarding,
      refreshOnboarding,
      hasPendingSourceTodos,
    }),
    [
      onboardingStatus,
      shouldShowModal,
      isModalOpen,
      openModal,
      closeModal,
      onboardingData,
      loading,
      updateOnboarding,
      completeOnboarding,
      refreshOnboarding,
      hasPendingSourceTodos,
    ]
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error("useOnboarding must be used within an OnboardingProvider");
  }
  return context;
}

/** Toggle onboarding on/off (persisted in localStorage). */
export function toggleOnboardingFlag(): boolean {
  const next = !readOnboardingFlag();
  localStorage.setItem(STORAGE_KEY, String(next));
  window.dispatchEvent(new Event("verity_onboarding_toggle"));
  return next;
}

export { readOnboardingFlag as isOnboardingEnabled };
